import type { Bone } from "@babylonjs/core/Bones/bone";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { Scene } from "@babylonjs/core/scene";
import {
  BASKETBALL_SWEET_WINDOW,
  TENNIS_REACH,
  TENNIS_REACH_HEIGHT,
  basketballSweetSpot,
  tennisAimFromFacing,
  type SportsState,
  type TennisAim,
  type TennisCourtWire,
} from "@townsy/contracts";
import { SPORTS_BASKETBALL, SPORTS_SOCCER, SPORTS_TENNIS, insideSportsBox } from "@townsy/world";
import { PartBatch, box, cylinder, torus } from "../../scene/core/meshKit";
import { chargePower, useSportsAim, type SwingCue } from "./aimStore";

/**
 * Townsy Sports Center, round 2 (founder feedback 2026-10-13: "Neither player has a racket ... nor an option to swing;
 * for basketball there is no aiming; soccer needs more fine control and angling"): what the 3D adds on top of the
 * courts to make the games readable and aimable. All of it only DISPLAYS; the server decides.
 *  - Rackets in the right hand of everyone playing tennis (people: attached to their avatar's forearm bone; the AI: its
 *    own arm), and a visible forehand / backhand swing (the avatar's arm bones are posed for the swing after the
 *    avatar's own walk pose, every frame of the swing) whenever the server counts a swing.
 *  - Tennis timing cue for the visitor: a reach ring at their feet and a ring under the ball, green while a swing would
 *    reach it; the aim marker on the far court (from the way they face).
 *  - Basketball: the aim arc while charging (green when the power is in the sweet band) and a ring at the pass target.
 *  - Soccer: the aim arrow from the ball (the way you face plus the A / D offset; longer with power).
 * It also tells the overlay what it sees from the visitor's own avatar (aimStore.setSeen).
 */
export interface DrawnBalls {
  basketball: Vector3;
  soccer: Vector3;
  tennis: Array<Vector3 | null>;
}

export interface SportsAids {
  update(state: SportsState): void;
  frame(dtS: number, nowMs: number, balls: DrawnBalls): void;
  /** The AI tennis players' right arms (swing pivots at the shoulder), set by the scene after it builds them. */
  setAiArms(arms: Array<TransformNode | null>): void;
  /** The racket materials (the scene builds the AI players' rackets with them). */
  readonly racketMaterials: { strings: StandardMaterial; frame: StandardMaterial };
  dispose(): void;
}

const ARM_R = 8;
const FOREARM_R = 9;
/** Hand position in the forearm bone's frame (forearm 0.25 m + half a hand). */
const HAND_IN_FOREARM = new Vector3(0, -0.28, 0.02);
export const RACKET_IDLE_TILT = -2.3;
/** At rest the racket is held ready: head up, forward and out to the right of the body (so it shows from behind too). */
export const RACKET_IDLE_ROLL = 0.6;
const SWING_MS = 420;
/** HARNESS ONLY: when set, every swing holds at this point (0..1) so a photo can show the pose. Never set by the app. */
export const SWING_PHOTO: { hold: number | null } = { hold: null };
const swingT = (start: number, now: number): number => SWING_PHOTO.hold ?? (now - start) / SWING_MS;

/** Arm pose keyframes (t, yaw, roll) of a right-handed forehand / backhand, in the parent's frame. */
const SWING_KEYS = {
  fore: [
    [0, 0, 0.1],
    [0.25, 1.3, 1.2],
    [0.6, -1.3, 1.2],
    [0.85, -1.9, 1.0],
    [1, -1.2, 0.6],
  ],
  back: [
    [0, 0, 0.1],
    [0.25, 2.6, 1.05],
    [0.6, 4.4, 1.2],
    [0.85, 5.5, 1.1],
    [1, 5.9, 0.6],
  ],
} as const;

/** Swing pose at t (0..1): yaw and roll of the upper arm, and how strongly it overrides the walking pose. */
export function swingPose(hand: "fore" | "back", t: number): { yaw: number; roll: number; weight: number } {
  const keys = SWING_KEYS[hand];
  const c = Math.max(0, Math.min(1, t));
  let i = 0;
  while (i < keys.length - 2 && c > keys[i + 1]![0]) i++;
  const a = keys[i]!;
  const b = keys[i + 1]!;
  const k = (c - a[0]) / (b[0] - a[0] || 1);
  const weight = c < 0.12 ? c / 0.12 : c > 0.85 ? Math.max(0, (1 - c) / 0.15) : 1;
  return { yaw: a[1] + (b[1] - a[1]) * k, roll: a[2] + (b[2] - a[2]) * k, weight };
}

interface Swing {
  start: number;
  hand: "fore" | "back";
}

interface HumanRacket {
  actorId: string;
  pivot: TransformNode;
  root: TransformNode;
  racket: Mesh;
  body: AbstractMesh | null;
  swing: Swing | null;
}

/** An original tennis racket: grip, throat, oval frame and strings; it extends along -y from the hand (0, 0, 0). */
export function buildRacket(scene: Scene, name: string, strings: StandardMaterial, frameMat: StandardMaterial, frameColor = "#D9412B"): { root: TransformNode; meshes: Mesh[] } {
  const root = new TransformNode(name, scene);
  const b = new PartBatch();
  b.add(cylinder(scene, `${name}Grip`, { height: 0.2, diameter: 0.034, tessellation: 10 }, "#26282E", { pos: { y: -0.1 } }));
  b.add(box(scene, `${name}ThroatL`, 0.018, 0.13, 0.018, frameColor, { pos: { x: -0.04, y: -0.25 }, rot: { z: -0.28 } }));
  b.add(box(scene, `${name}ThroatR`, 0.018, 0.13, 0.018, frameColor, { pos: { x: 0.04, y: -0.25 }, rot: { z: 0.28 } }));
  const ring = torus(scene, `${name}Head`, 0.27, 0.022, 24, frameColor, { pos: { y: -0.47 }, rot: { x: Math.PI / 2 } });
  ring.scaling.set(1, 1, 1.25); // an oval: taller along the racket
  b.add(ring);
  const frame = b.build(`${name}Frame`, scene)!;
  frame.material = frameMat;
  frame.parent = root;
  frame.isPickable = false;
  const face = CreatePlane(`${name}Strings`, { width: 0.25, height: 0.31, sideOrientation: Mesh.DOUBLESIDE }, scene);
  face.position.y = -0.47;
  face.material = strings;
  face.parent = root;
  face.isPickable = false;
  return { root, meshes: [frame, face] };
}

export function buildSportsAids(scene: Scene, parent: TransformNode, localActorId: () => string | null, netTexture: Texture): SportsAids {
  const meshes: Mesh[] = [];
  const materials: StandardMaterial[] = [];
  const nodes: TransformNode[] = [];
  const mat = (name: string, color: string, alpha = 1, emissive = 0.6): StandardMaterial => {
    const m = new StandardMaterial(name, scene);
    m.diffuseColor = Color3.FromHexString(color);
    m.emissiveColor = Color3.FromHexString(color).scale(emissive);
    m.specularColor = Color3.Black();
    m.alpha = alpha;
    if (alpha < 1) m.backFaceCulling = false;
    materials.push(m);
    return m;
  };
  const own = <T extends Mesh>(m: T): T => {
    m.parent = parent;
    m.isPickable = false;
    m.setEnabled(false);
    meshes.push(m);
    return m;
  };

  // ---- materials ----
  const green = mat("sportsAidGreen", "#2FBF71", 0.85, 0.8);
  const amber = mat("sportsAidAmber", "#F2B233", 0.85, 0.8);
  const red = mat("sportsAidRed", "#E8622A", 0.85, 0.8);
  const white = mat("sportsAidWhite", "#FFFFFF", 0.55, 0.9);
  const cobalt = mat("sportsAidCobalt", "#2851D9", 0.5, 0.7);
  const strings = new StandardMaterial("sportsRacketStrings", scene);
  const stringTex = netTexture.clone();
  stringTex.uScale = 2.2;
  stringTex.vScale = 2.8;
  strings.diffuseTexture = stringTex;
  strings.opacityTexture = stringTex;
  strings.emissiveColor = new Color3(0.6, 0.6, 0.6);
  strings.specularColor = Color3.Black();
  strings.backFaceCulling = false;
  materials.push(strings);
  const racketFrame = new StandardMaterial("sportsRacketFrame", scene);
  racketFrame.diffuseColor = Color3.White();
  racketFrame.specularColor = new Color3(0.25, 0.25, 0.25);
  racketFrame.emissiveColor = new Color3(0.12, 0.12, 0.12);
  materials.push(racketFrame);

  // ---- rackets for people on a court side ----
  const rackets = new Map<string, HumanRacket>();
  let racketSeq = 0;
  const addRacket = (actorId: string): HumanRacket => {
    const pivot = new TransformNode(`sportsRacketPivot${racketSeq}`, scene);
    const built = buildRacket(scene, `sportsRacket${racketSeq++}`, strings, racketFrame);
    built.root.parent = pivot;
    built.root.rotation.set(RACKET_IDLE_TILT, 0, RACKET_IDLE_ROLL);
    const r: HumanRacket = { actorId, pivot, root: built.root, racket: built.meshes[0]!, body: null, swing: null };
    rackets.set(actorId, r);
    return r;
  };
  const removeRacket = (r: HumanRacket) => {
    rackets.delete(r.actorId);
    r.pivot.dispose(false, false);
  };
  /** Keep the racket on the avatar's forearm (the body is rebuilt when an appearance changes). */
  const attach = (r: HumanRacket): boolean => {
    const body = scene.getMeshByName(`av_${r.actorId}_body`);
    if (!body || !body.skeleton) {
      r.pivot.setEnabled(false);
      return false;
    }
    if (r.body !== body || body.isDisposed()) {
      const bone = body.skeleton.bones[FOREARM_R];
      if (!bone) return false;
      r.pivot.attachToBone(bone, body);
      r.pivot.position.copyFrom(HAND_IN_FOREARM);
      r.pivot.rotation.set(0, 0, 0);
      r.body = body;
    }
    r.pivot.setEnabled(body.isEnabled());
    return true;
  };

  // ---- AI arms (built by the scene) ----
  let aiArms: Array<TransformNode | null> = [];
  const aiSwing: Array<Swing | null> = [null, null];

  // ---- swings from the server's counters ----
  const seen = new Map<string, number>();
  let primed = false;

  // ---- tennis cue and aim marker ----
  const reachRing = own(CreateTorus("sportsReachRing", { diameter: TENNIS_REACH * 2, thickness: 0.05, tessellation: 48 }, scene));
  const ballRing = own(CreateTorus("sportsBallRing", { diameter: 0.7, thickness: 0.06, tessellation: 28 }, scene));
  const aimDisc = own(CreateDisc("sportsAimDisc", { radius: 0.95, tessellation: 32 }, scene));
  aimDisc.rotation.x = Math.PI / 2;
  aimDisc.material = mat("sportsAidTarget", "#FFFFFF", 0.35, 0.9);
  const aimRing = own(CreateTorus("sportsAimRing", { diameter: 2.3, thickness: 0.07, tessellation: 36 }, scene));
  aimRing.material = mat("sportsAidTargetRing", "#FFD34D", 0.95, 0.9);

  // ---- basketball aim arc and pass ring ----
  const ARC_DOTS = 18;
  const arc = Array.from({ length: ARC_DOTS }, (_, i) => own(CreateSphere(`sportsArcDot${i}`, { diameter: i === ARC_DOTS - 1 ? 0.2 : 0.1, segments: 6 }, scene)));
  const passRing = own(CreateTorus("sportsPassRing", { diameter: 1.3, thickness: 0.07, tessellation: 32 }, scene));
  passRing.material = cobalt;

  // ---- soccer aim arrow (shaft along +z from the ball, head at the end) ----
  const arrowRoot = new TransformNode("sportsArrow", scene);
  arrowRoot.parent = parent;
  nodes.push(arrowRoot);
  const shaft = own(CreatePlane("sportsArrowShaft", { width: 0.16, height: 1, sideOrientation: Mesh.DOUBLESIDE }, scene));
  shaft.parent = arrowRoot;
  shaft.rotation.x = Math.PI / 2;
  const headB = new PartBatch();
  headB.add(cylinder(scene, "sportsArrowHeadCone", { height: 0.5, diameterTop: 0, diameterBottom: 0.55, tessellation: 3 }, "#FFFFFF", { rot: { x: Math.PI / 2 } }));
  const head = own(headB.build("sportsArrowHead", scene)!);
  head.parent = arrowRoot;
  head.scaling.y = 0.2; // flat

  let state: SportsState | null = null;
  let avatarCache: { at: number; nodes: TransformNode[] } = { at: -Infinity, nodes: [] };
  const avatars = (now: number): TransformNode[] => {
    if (now - avatarCache.at > 1000) avatarCache = { at: now, nodes: scene.transformNodes.filter((n) => /^av_act_[A-Za-z0-9]+$/.test(n.name)) };
    return avatarCache.nodes;
  };
  const nodeOf = (actorId: string | null): TransformNode | null => (actorId ? scene.getTransformNodeByName(`av_${actorId}`) : null);

  const q = new Quaternion();
  const qRig = new Quaternion();
  const scl = new Vector3();
  const tr = new Vector3();
  const one = new Vector3(1, 1, 1);
  /** Pose a bone for the swing, blended with whatever the avatar's own animation set this frame. */
  const poseBone = (bone: Bone, yaw: number, roll: number, weight: number) => {
    const m = bone.getLocalMatrix();
    m.decompose(scl, qRig, tr);
    Quaternion.RotationYawPitchRollToRef(yaw, 0, roll, q);
    Quaternion.SlerpToRef(qRig, q, weight, q);
    Matrix.ComposeToRef(one, q, tr, m);
    bone.markAsDirty();
  };
  const poseStraight = (bone: Bone, weight: number) => {
    const m = bone.getLocalMatrix();
    m.decompose(scl, qRig, tr);
    Quaternion.SlerpToRef(qRig, Quaternion.Identity(), weight, q);
    Matrix.ComposeToRef(one, q, tr, m);
    bone.markAsDirty();
  };

  // The avatars' own walk pose is applied every frame before rendering; the swing is applied after it, just before
  // the skeletons are computed for this frame.
  let nowMs = 0;
  const beforeRender: Observer<Scene> | null = scene.onBeforeRenderObservable.add(() => {
    for (const r of rackets.values()) {
      if (!r.swing || !r.body?.skeleton) continue;
      const t = swingT(r.swing.start, nowMs);
      if (t >= 1) {
        r.swing = null;
        continue;
      }
      const p = swingPose(r.swing.hand, t);
      const bones = r.body.skeleton.bones;
      if (bones[ARM_R]) poseBone(bones[ARM_R]!, p.yaw, p.roll, p.weight);
      if (bones[FOREARM_R]) poseStraight(bones[FOREARM_R]!, p.weight);
    }
  });

  const startSwing = (court: number, sideWire: TennisCourtWire["sides"][number], hand: "fore" | "back") => {
    if (sideWire.kind === "ai") aiSwing[court] = { start: nowMs, hand };
    else if (sideWire.actorId) {
      const r = rackets.get(sideWire.actorId);
      if (r) r.swing = { start: nowMs, hand };
    }
  };

  const update = (s: SportsState): void => {
    state = s;
    // Rackets for every person on a court side.
    const players = new Set<string>();
    for (const c of s.tennis) for (const side of c.sides) if (side.kind === "human" && side.actorId) players.add(side.actorId);
    for (const r of [...rackets.values()]) if (!players.has(r.actorId)) removeRacket(r);
    for (const id of players) if (!rackets.has(id)) addRacket(id);
    // New swings.
    s.tennis.forEach((c, ci) => {
      ([0, 1] as const).forEach((si) => {
        const sw = c.swings?.[si];
        if (!sw) return;
        const key = `${ci}:${si}`;
        const before = seen.get(key);
        seen.set(key, sw.n);
        if (primed && before !== undefined && sw.n > before) startSwing(ci, c.sides[si], sw.hand);
      });
    });
    primed = true;
  };

  const show = (m: Mesh, on: boolean) => {
    if (m.isEnabled() !== on) m.setEnabled(on);
  };

  const frame = (_dtS: number, now: number, balls: DrawnBalls): void => {
    nowMs = now;
    const st = state;
    const me = localActorId();
    const meNode = nodeOf(me);
    const aim = useSportsAim.getState();

    // Rackets follow their hands; the idle tilt straightens while swinging.
    for (const r of rackets.values()) {
      if (!attach(r)) continue;
      const racketRoot = r.root;
      if (!racketRoot) continue;
      const swinging = r.swing ? Math.min(1, swingPose(r.swing.hand, swingT(r.swing.start, now)).weight) : 0;
      racketRoot.rotation.set(RACKET_IDLE_TILT * (1 - swinging), 0, RACKET_IDLE_ROLL * (1 - swinging));
    }
    // The AI's arm swings around its shoulder.
    aiArms.forEach((arm, i) => {
      if (!arm) return;
      const sw = aiSwing[i];
      if (!sw) {
        arm.rotation.set(0, 0, 0.12);
        return;
      }
      const t = swingT(sw.start, now);
      if (t >= 1) {
        aiSwing[i] = null;
        return;
      }
      const p = swingPose(sw.hand, t);
      arm.rotation.set(0, p.yaw * p.weight, 0.12 + (p.roll - 0.12) * p.weight);
      const racketRoot = arm.getChildTransformNodes(false).find((n) => n.name.endsWith("RacketRoot"));
      if (racketRoot) racketRoot.rotation.set(RACKET_IDLE_TILT * (1 - p.weight), 0, RACKET_IDLE_ROLL * (1 - p.weight));
    });

    // What the visitor's own avatar sees.
    const seenPatch: { hoopDistance: number | null; tennisAim: TennisAim; swingCue: SwingCue; facing: number | null } = {
      hoopDistance: null,
      tennisAim: 0,
      swingCue: "none",
      facing: null,
    };
    if (meNode) {
      const p = meNode.position;
      const facing = meNode.rotation.y;
      seenPatch.facing = Math.round(facing * 100) / 100;
      seenPatch.tennisAim = tennisAimFromFacing(facing);
      if (insideSportsBox(SPORTS_BASKETBALL.court, p.x, p.z, 1)) {
        const hoop = SPORTS_BASKETBALL.hoops.reduce((a, h) => (Math.hypot(h.x - p.x, h.z - p.z) < Math.hypot(a.x - p.x, a.z - p.z) ? h : a));
        seenPatch.hoopDistance = Math.round(Math.hypot(hoop.x - p.x, hoop.z - p.z) * 10) / 10;
      }
    }

    // ---- tennis: reach ring, ball ring, aim marker ----
    let showReach = false;
    let showBallRing = false;
    let showAim = false;
    if (st && me && meNode) {
      st.tennis.forEach((c, ci) => {
        const side = c.sides[0].actorId === me ? 0 : c.sides[1].actorId === me ? 1 : null;
        if (side === null) return;
        const geo = SPORTS_TENNIS.courts[ci]!;
        const ball = balls.tennis[ci];
        const p = meNode.position;
        const myServe = c.phase === "serve" && c.server === side;
        const coming = c.phase === "rally" && c.lastHitter !== null && c.lastHitter !== side && c.bounces <= 1;
        let ready = myServe;
        if (coming && ball) {
          const onMySide = side === 0 ? ball.z <= geo.netZ : ball.z >= geo.netZ;
          ready = onMySide && ball.y <= TENNIS_REACH_HEIGHT && Math.hypot(ball.x - p.x, ball.z - p.z) <= TENNIS_REACH;
        }
        seenPatch.swingCue = ready ? "ready" : coming ? "coming" : "none";
        if (c.phase === "rally" || c.phase === "serve") {
          showReach = true;
          reachRing.position.set(p.x, 0.04, p.z);
          reachRing.material = ready ? green : white;
        }
        if ((coming || myServe) && ball) {
          showBallRing = true;
          ballRing.position.set(ball.x, myServe ? ball.y : 0.035, ball.z);
          ballRing.material = ready ? green : amber;
        }
        if (coming || myServe) {
          showAim = true;
          const halfW = (geo.court.maxX - geo.court.minX) / 2;
          const dirZ = side === 0 ? 1 : -1;
          const x = geo.centreX + seenPatch.tennisAim * (halfW - 1.6);
          const z = geo.netZ + dirZ * 12 * 0.62;
          aimDisc.position.set(x, 0.03, z);
          aimRing.position.set(x, 0.04, z);
        }
      });
    }
    show(reachRing, showReach);
    show(ballRing, showBallRing);
    show(aimDisc, showAim);
    show(aimRing, showAim);

    // ---- basketball: the aim arc while charging, the pass target otherwise ----
    const holding = Boolean(st && me && st.basketball.phase === "held" && st.basketball.holder === me);
    const charging = aim.charging;
    let arcOn = false;
    if (holding && charging?.kind === "shoot" && meNode) {
      const p = meNode.position;
      const hoop = SPORTS_BASKETBALL.hoops.reduce((a, h) => (Math.hypot(h.x - p.x, h.z - p.z) < Math.hypot(a.x - p.x, a.z - p.z) ? h : a));
      const d = Math.hypot(hoop.x - p.x, hoop.z - p.z) || 0.01;
      const dir = { x: (hoop.x - p.x) / d, z: (hoop.z - p.z) / d };
      const power = chargePower(charging.startedAt, now);
      const err = power - basketballSweetSpot(d);
      const reach = d + err * 4;
      const T = Math.max(0.8, Math.min(1.6, 0.75 + 0.06 * d));
      const y0 = 2.2;
      const y1 = SPORTS_BASKETBALL.rimHeight + 0.05;
      const vy = (y1 - y0 + 0.5 * 9.81 * T * T) / T;
      const m = Math.abs(err) <= BASKETBALL_SWEET_WINDOW ? green : Math.abs(err) <= 0.15 ? amber : red;
      arc.forEach((dot, i) => {
        const f = (i + 1) / ARC_DOTS;
        const t = T * f;
        dot.position.set(p.x + dir.x * (0.3 + (reach - 0.3) * f), y0 + vy * t - 0.5 * 9.81 * t * t, p.z + dir.z * (0.3 + (reach - 0.3) * f));
        dot.material = m;
      });
      arcOn = true;
    }
    for (const dot of arc) show(dot, arcOn);
    let passOn = false;
    if (holding && !charging && meNode) {
      const p = meNode.position;
      const f = meNode.rotation.y;
      let best: TransformNode | null = null;
      let bestD = Infinity;
      for (const n of avatars(now)) {
        if (n === meNode || n.isDisposed() || !n.isEnabled()) continue;
        const q2 = n.position;
        if (!insideSportsBox(SPORTS_BASKETBALL.court, q2.x, q2.z, 1)) continue;
        const d = Math.hypot(q2.x - p.x, q2.z - p.z);
        if (d > 12 || d < 0.3) continue;
        let off = Math.atan2(q2.x - p.x, q2.z - p.z) - f;
        off = Math.atan2(Math.sin(off), Math.cos(off));
        if (Math.abs(off) > Math.PI / 3 || d >= bestD) continue;
        best = n;
        bestD = d;
      }
      if (best) {
        passRing.position.set(best.position.x, 0.05, best.position.z);
        passOn = true;
      }
    }
    show(passRing, passOn);

    // ---- soccer: the aim arrow near the ball ----
    let arrowOn = false;
    if (st && meNode && st.soccer.phase === "live") {
      const b = balls.soccer;
      const p = meNode.position;
      if (Math.hypot(b.x - p.x, b.z - p.z) < 3 && insideSportsBox(SPORTS_SOCCER.pitch, p.x, p.z, 1)) {
        const yaw = meNode.rotation.y + aim.aimOffset;
        const kicking = charging?.kind === "kick";
        const power = kicking ? chargePower(charging.startedAt, now) : 0;
        const len = kicking ? 2 + 8 * power : 2.6;
        arrowRoot.position.set(b.x, 0.06, b.z);
        arrowRoot.rotation.y = yaw;
        shaft.scaling.y = len;
        shaft.position.z = 0.3 + len / 2;
        head.position.z = 0.3 + len + 0.2;
        const m = kicking ? (power > 0.85 ? red : power > 0.55 ? amber : green) : white;
        shaft.material = m;
        head.material = m;
        arrowOn = true;
      }
    }
    show(shaft, arrowOn);
    show(head, arrowOn);

    aim.setSeen(seenPatch);
  };

  return {
    update,
    frame,
    racketMaterials: { strings, frame: racketFrame },
    setAiArms(arms) {
      aiArms = arms;
    },
    dispose() {
      if (beforeRender) scene.onBeforeRenderObservable.remove(beforeRender);
      for (const r of [...rackets.values()]) removeRacket(r);
      for (const m of meshes) if (!m.isDisposed()) m.dispose(false, false);
      for (const n of nodes) if (!n.isDisposed()) n.dispose();
      for (const m of materials) m.dispose(false, false);
      stringTex.dispose();
      useSportsAim.getState().setSeen({ hoopDistance: null, swingCue: "none", facing: null });
    },
  };
}
